<template>
  <div class="content__item-form-type content__item-form-type--text">
    <div
      v-if="!model.state.maskType"
      class="content__item-form-group content__item-form-group--date form-group"
      :class="fieldClasses"
    >
      <label class="content__item-form-label content__item-form-label--date">{{ model.state.label }}</label>
      <div ref="wrapperRef" class="content__item-date-wrapper" tabindex="0" @focus="onFocus" @blur="onBlur">
        <flat-pickr
          v-model="state.value"
          :name="`form[${model.state.id}]`"
          :value="state.value"
          :config="config"
          class="form-control"
        ></flat-pickr>
      </div>
    </div>
    <div
      v-if="model.state.maskType"
      class="content__item-form-group content__item-form-group--mask form-group"
      :class="fieldClasses"
    >
      <label class="content__item-form-label content__item-form-label--mask">{{ model.state.label }}</label>
      <div class="content__item-date-wrapper">
        <input
          v-if="!yearFirst"
          v-model="state.value"
          v-maska:[vMaskOptions]
          tabindex="0"
          :placeholder="maskaPlaceholder"
          class="content__item-form-mask form-control"
          :name="`form[${model.state.id}]`"
          type="text"
          :aria-label="model.state.label"
          @focus="onFocus"
          @blur="onBlur"
        />
        <input
          v-if="yearFirst"
          v-model="state.value"
          v-maska:[vMaskOptions]
          tabindex="0"
          :placeholder="maskaPlaceholder"
          class="content__item-form-mask form-control"
          :name="`form[${model.state.id}]`"
          type="text"
          :aria-label="model.state.label"
          @focus="onFocus"
          @blur="onBlur"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted, ref } from 'vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import 'flatpickr/dist/plugins/confirmDate/confirmDate.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import { vMaska } from 'maska';
import { addDays } from 'date-fns';
import type { FormElementDateModel } from '@/src/components/addons/registration/Models/FormElementDateModel';
import { convertDateFormat } from '@/src/utilities/DateHelpers';

interface Config {
  altInput: boolean;
  altFormat: string;
  dateFormat: string;
  locale: {
    firstDayOfWeek: number;
  };
  enableTime?: boolean;
  enableSeconds?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  disable: string[];
}

export default defineComponent({
  name: 'FormElementDate',
  components: {
    flatPickr
  },
  directives: { maska: vMaska },
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<FormElementDateModel>,
      required: true
    },

    showValidationErrors: {
      type: Boolean,
      default: false
    }
  },
  setup: function (props) {
    const state = props.model.state;
    const wrapperRef = ref<HTMLElement | undefined>();

    const isFocus = ref(false);
    const yearFirst = ref(false);
    const config = computed(() => {
      const ConfigData: Config = {
        altInput: true,
        altFormat: state.fieldFormat || 'Y-m-d',
        dateFormat: state.fieldFormat || 'Y-m-d',
        locale: {
          firstDayOfWeek: 1
        },
        enableTime: state.time.enabled,
        enableSeconds: state.time.enabled,
        ...(validationMinDate.value && { minDate: validationMinDate.value }),
        ...(validationMaxDate.value && { maxDate: validationMaxDate.value }),
        disable: [],
        ...(state.time.min && { minTime: state.time.min }),
        ...(state.time.max && { maxTime: state.time.max })
      };

      if (state.time.enabled) {
        const timeFormat = ' h:i K';
        ConfigData.altFormat = state.fieldFormat + timeFormat;
        ConfigData.dateFormat = `${state.fieldFormat} H:i`;
      }
      if (state.disableDates) {
        ConfigData.disable = state.disabledDatesValues;
      }

      return ConfigData;
    });

    if (state.fieldFormat === 'Y-m-d' || state.fieldFormat === 'Y.m.d' || state.fieldFormat === 'Y/m/d') {
      yearFirst.value = true;
    }

    const validationMinDate = computed(() => {
      if (state.datesFromTomorrow) {
        return addDays(new Date(), 1).toISOString();
      }
      return state.minDate ? convertDateFormat(state.minDate, state.fieldFormat || 'Y-m-d') : undefined;
    });

    const validationMaxDate = computed(() => {
      return state.maxDate ? convertDateFormat(state.maxDate, state.fieldFormat || 'Y-m-d') : undefined;
    });

    // Maska contains MaskaOptions. But their module doesn't export it correctly.
    // So we're leaving this untyped.
    const vMaskOptions = computed(() => {
      return {
        mask: state.mask
      };
    });

    const maskaPlaceholder = computed(() => {
      if (state.placeholder) {
        return state.placeholder;
      }

      let placeholder = '____-__-__';
      if (state.fieldFormat === 'Y-m-d' || state.fieldFormat === 'Y.m.d' || state.fieldFormat === 'Y/m/d') {
        placeholder = '____-__-__';
      } else {
        placeholder = '__-__-____';
      }
      if (state.fieldFormat.includes('.') && state.fieldFormat === 'Y.m.d') {
        placeholder = '____.__.__';
      } else if (state.fieldFormat.includes('/') && state.fieldFormat === 'Y/m/d') {
        placeholder = '____/__/__';
      } else if (state.fieldFormat.includes('/') && state.fieldFormat === 'd/m/Y') {
        placeholder = '__/__/____';
      } else if (state.fieldFormat.includes('.') && state.fieldFormat === 'd.m.Y') {
        placeholder = '__.__.____';
      }
      return placeholder;
    });

    const onBlur = () => {
      isFocus.value = false;
    };

    const onFocus = () => {
      isFocus.value = true;
    };

    const fieldClasses = computed(() => {
      return {
        'form-group--error': props.showValidationErrors && !state.isValid,
        'form-group--has-value': state.value && state.value !== '',
        'form-group--is-focus': isFocus.value,
        'form-group--valid': !!state.isValid
      };
    });

    const setAriaLabel = () => {
      const flatpickrElement = wrapperRef.value;
      if (!flatpickrElement) {
        return;
      }
      const inputElements = flatpickrElement.querySelectorAll('input');

      inputElements.forEach((inputElement) => {
        if (!inputElement.hidden) {
          inputElement.setAttribute('aria-label', props.model.state.label);
        }
      });
    };

    onMounted(() => {
      setAriaLabel();
    });

    return {
      wrapperRef,
      state,
      maskaPlaceholder,
      vMaskOptions,
      yearFirst,
      fieldClasses,
      isFocus,
      onBlur,
      onFocus,
      config
    };
  }
});
</script>

<style lang="scss">
.flatpickr-disabled {
  opacity: 0.5;
}
.flatpickr-calendar.leadfamly-calendar-datepicker {
  width: 290px !important;
  height: 290px !important;
  border-radius: 0 !important;
  border: 1px solid #e5e5e5 !important;
}

.flatpickr-months {
  padding: 4px 16px 8px !important;
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: bold !important;
}

.flatpickr-current-month {
  .flatpickr-monthDropdown-months {
    font-weight: bold !important;
    margin-right: 4px !important;

    &:hover {
      background: transparent !important;
    }
  }

  .numInputWrapper,
  .flatpickr-monthDropdown-months {
    font-size: 14px !important;
  }

  .numInputWrapper {
    width: 7ch !important;

    &:hover {
      background: transparent !important;
    }
  }
}

.flatpickr-current-month input.cur-year {
  font-weight: bold !important;
}

.numInputWrapper:hover {
  background: transparent !important;
}

.flatpickr-weekdays span.flatpickr-weekday {
  color: rgba(0, 0, 0, 0.38) !important;
  font-size: 12px !important;
}

.flatpickr-months {
  .flatpickr-next-month:hover svg,
  .flatpickr-prev-month:hover svg {
    color: rgba(0, 0, 0, 0.9) !important;
    fill: rgba(0, 0, 0, 0.9) !important;
  }
}

.flatpickr-innerContainer {
  padding: 0 12px !important;
}

.flatpickr-days {
  width: 100% !important;
}

.dayContainer {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
}

.flatpickr-day {
  color: rgba(0, 0, 0, 0.87) !important;
  cursor: pointer !important;
  width: 32px !important;
  flex-basis: 32px !important;
  max-width: 32px !important;
  height: 32px !important;
  margin: 1px 2px !important;
  line-height: 32px !important;
  font-size: 12px !important;
}
</style>
